import { Pencil } from "lucide-react";
import type { UserListItem, UserRole } from "@/features/user/types";
import "./UserDirectory.css";

export const USER_ROLE_LABELS: Record<UserRole, string> = {
  super: "Admin",
  admin_helper: "Auxiliar administrativo",
  checker: "Conferente",
  client: "Cliente",
  client_store: "Responsável por loja",
  supplier: "Fornecedor",
  overviewer: "Visualizador",
};

function linkLabel(user: UserListItem) {
  if (user.client_name) return user.client_name;
  if (user.supplier_name) return user.supplier_name;
  if (user.client_id || user.supplier_id) return "Vínculo não informado";
  return "Sem vínculo";
}

type UserDirectoryProps = {
  users: UserListItem[];
  currentUserId?: string;
  isFetching?: boolean;
  canEdit: (user: UserListItem) => boolean;
  onEdit: (user: UserListItem) => void;
};

export function UserDirectory({
  users,
  currentUserId,
  isFetching = false,
  canEdit,
  onEdit,
}: UserDirectoryProps) {
  return (
    <div
      role="table"
      aria-label="Diretório de usuários"
      aria-busy={isFetching}
      className="users-ledger operational-directory operational-directory__updating"
      data-testid="users-ledger"
    >
      <div role="row" className="users-ledger__row users-ledger__header">
        <span role="columnheader">Usuário</span>
        <span role="columnheader">Perfil</span>
        <span role="columnheader">Vínculo</span>
        <span role="columnheader">Estado</span>
        <span role="columnheader" className="users-ledger__action-heading">
          Editar
        </span>
      </div>

      {users.map((user) => {
        const editable = canEdit(user);
        return (
          <div
            key={user.id}
            role="row"
            className="users-ledger__row users-ledger__data-row"
          >
            <span role="cell" className="users-ledger__identity">
              <span className="users-ledger__name">
                {user.name}
                {user.id === currentUserId ? (
                  <span className="users-ledger__self"> (você)</span>
                ) : null}
              </span>
              <span className="users-ledger__email">{user.email}</span>
            </span>
            <span role="cell" className="users-ledger__role">
              {USER_ROLE_LABELS[user.role]}
            </span>
            <span role="cell" className="users-ledger__link">
              {linkLabel(user)}
            </span>
            <span
              role="cell"
              className="users-ledger__state"
              data-inactive={user.deactivated_at ? "true" : undefined}
            >
              {user.deactivated_at ? "Desativado" : "Ativo"}
            </span>
            <span role="cell" className="users-ledger__action-cell">
              {editable ? (
                <button
                  type="button"
                  className="users-ledger__edit"
                  aria-label={`Editar ${user.name}`}
                  onClick={() => onEdit(user)}
                >
                  <Pencil aria-hidden="true" size={16} strokeWidth={2} />
                </button>
              ) : (
                <span className="users-ledger__locked" aria-label="Sem edição">
                  —
                </span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function UserDirectorySkeleton() {
  return (
    <div
      className="users-ledger users-ledger--skeleton operational-directory"
      data-testid="users-ledger-skeleton"
      aria-label="Carregando usuários"
    >
      <div className="users-ledger__skeleton-header" />
      {Array.from({ length: 6 }, (_, index) => (
        <div className="users-ledger__skeleton-row" key={index}>
          <span />
          <span />
          <span />
          <span />
        </div>
      ))}
    </div>
  );
}
